import React, { useEffect, useState } from "react";
import { api, apiErr, fmtMoney, fmtDate } from "../lib/api";
import { PageHeader, StatusBadge } from "../components/ResourcePage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { CustomerPicker } from "../components/CustomerPicker";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({
  invoice_number: `INV-${Math.floor(1000 + Math.random() * 9000)}`,
  customer_name: "", customer_id: "", issue_date: today(), due_date: today(),
  status: "draft", tax_rate: 0, notes: "",
  line_items: [{ description: "", quantity: 1, unit_price: 0 }],
});

function calcTotal(f) {
  const sub = f.line_items.reduce((s, li) => s + Number(li.quantity || 0) * Number(li.unit_price || 0), 0);
  return sub + (sub * Number(f.tax_rate || 0)) / 100;
}

export function Invoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [delId, setDelId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/invoices"); setItems(data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(blank()); setEditing(null); setOpen(true); };
  const openEdit = (row) => {
    setForm({ ...row, line_items: row.line_items?.length ? row.line_items : [{ description: "", quantity: 1, unit_price: 0 }] });
    setEditing(row.id); setOpen(true);
  };

  const setLi = (i, k, v) => {
    const li = [...form.line_items]; li[i] = { ...li[i], [k]: v }; setForm({ ...form, line_items: li });
  };
  const addLi = () => setForm({ ...form, line_items: [...form.line_items, { description: "", quantity: 1, unit_price: 0 }] });
  const rmLi = (i) => setForm({ ...form, line_items: form.line_items.filter((_, x) => x !== i) });

  const save = async () => {
    const payload = {
      ...form, tax_rate: Number(form.tax_rate || 0),
      line_items: form.line_items.map((li) => ({
        description: li.description, quantity: Number(li.quantity || 0), unit_price: Number(li.unit_price || 0),
      })),
    };
    try {
      if (editing) await api.put(`/invoices/${editing}`, payload);
      else await api.post("/invoices", payload);
      toast.success(editing ? "Invoice updated" : "Invoice created");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/invoices/${delId}`); toast.success("Deleted"); setDelId(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="fade-up">
      <PageHeader title="Invoices" subtitle="Create and track customer invoices."
        action={<Button onClick={openNew} data-testid="invoice-add"><Plus className="mr-1.5 h-4 w-4" />New invoice</Button>} />

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Issued</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="w-20 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-12 text-center text-muted-foreground">No invoices yet. Create your first one.</TableCell></TableRow>
            ) : items.map((row) => (
              <TableRow key={row.id} data-testid="invoice-row">
                <TableCell className="font-semibold">{row.invoice_number}</TableCell>
                <TableCell>{row.customer_name}</TableCell>
                <TableCell className="text-sm">{fmtDate(row.issue_date)}</TableCell>
                <TableCell className="text-sm">{fmtDate(row.due_date)}</TableCell>
                <TableCell><StatusBadge value={row.status} /></TableCell>
                <TableCell className="text-right num font-semibold">{fmtMoney(row.total)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row)} data-testid="invoice-edit"><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDelId(row.id)} data-testid="invoice-delete"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit invoice" : "New invoice"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2 sm:grid-cols-2">
            <div><Label className="text-xs">Invoice number</Label>
              <Input className="mt-1.5" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="invoice-number" /></div>
            <div><Label className="text-xs">Customer</Label>
              <div className="mt-1.5">
                <CustomerPicker name={form.customer_name} customerId={form.customer_id}
                  onChange={(name, id) => setForm({ ...form, customer_name: name, customer_id: id })}
                  testid="invoice-customer" />
              </div></div>
            <div><Label className="text-xs">Issue date</Label>
              <Input type="date" className="mt-1.5" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
            <div><Label className="text-xs">Due date</Label>
              <Input type="date" className="mt-1.5" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div><Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="invoice-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["draft", "sent", "paid", "overdue"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs">Tax rate (%)</Label>
              <Input type="number" className="mt-1.5" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="invoice-tax" /></div>
          </div>

          <div className="mt-2">
            <Label className="text-xs uppercase tracking-wider">Line items</Label>
            <div className="mt-2 space-y-2">
              {form.line_items.map((li, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input placeholder="Description" value={li.description} onChange={(e) => setLi(i, "description", e.target.value)} data-testid={`invoice-li-desc-${i}`} />
                  <Input type="number" placeholder="Qty" className="w-20" value={li.quantity} onChange={(e) => setLi(i, "quantity", e.target.value)} data-testid={`invoice-li-qty-${i}`} />
                  <Input type="number" placeholder="Price" className="w-28" value={li.unit_price} onChange={(e) => setLi(i, "unit_price", e.target.value)} data-testid={`invoice-li-price-${i}`} />
                  <span className="num w-24 text-right text-sm text-muted-foreground">{fmtMoney(Number(li.quantity || 0) * Number(li.unit_price || 0))}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => rmLi(i)} disabled={form.line_items.length === 1}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="mt-2" onClick={addLi} data-testid="invoice-add-line"><Plus className="mr-1 h-3.5 w-3.5" />Add line</Button>
          </div>

          <div className="mt-3"><Label className="text-xs">Notes</Label>
            <Textarea className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

          <div className="mt-2 flex items-center justify-between rounded-md bg-muted px-4 py-3">
            <span className="text-sm font-medium">Total</span>
            <span className="num font-display text-xl font-bold" data-testid="invoice-total">{fmtMoney(calcTotal(form))}</span>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="invoice-save">{editing ? "Save changes" : "Create invoice"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this invoice?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive" data-testid="invoice-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
